import { productRelease } from './productRelease';
// Bounded best-effort telemetry: no UUID creation, no sensitive input, no UI dependency.
const KEY = 'pill_product_events_v1';
const OBSERVATIONS = 'pill_product_observations_v1';
const DAILY_STATES = new Set(['purchase_eligible', 'notification_ready', 'notification_blocked',
    'notification_unknown', 'fullscreen_ready', 'fullscreen_blocked']);
function observationKey(uuid, name, release) {
    // The server additionally scopes this to the current group/account generation.
    const day = new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Seoul' });
    let runtime;
    try { runtime = JSON.parse(localStorage.getItem('pill_reminder_app_runtime_info') || '{}'); } catch {}
    return JSON.stringify([day, uuid, localStorage.getItem('pill_product_identity_epoch'), name,
        runtime?.platform, runtime?.appVersion, runtime?.channel, release]);
}

let running = false;
let timer;
export const analyticsId = () => window.crypto?.randomUUID?.() || `${Date.now().toString(36)}_${Math.random().toString(36).slice(2)}`;
const read = () => {
    try {
        const queue = JSON.parse(localStorage.getItem(KEY) || '[]');
        if (!Array.isArray(queue)) return [];
        return queue.filter(item => item && typeof item.uuid === 'string' && Number.isFinite(item.queuedAt)
            && item.event && typeof item.event.eventId === 'string' && typeof item.event.name === 'string').slice(-100);
    } catch { return []; }
};
function write(queue) { try { localStorage.setItem(KEY, JSON.stringify(queue.slice(-100))); } catch {} }
export function trackProduct(name, details = {}, eventId = analyticsId()) {
    try {
        const uuid = localStorage.getItem('pill_reminder_uuid');
        if (!uuid) return;
        const release = productRelease();
        let observations;
        let stateKey;
        if (DAILY_STATES.has(name)) {
            stateKey = observationKey(uuid, name, release);
            try { observations = JSON.parse(localStorage.getItem(OBSERVATIONS) || '[]'); } catch {}
            if (!Array.isArray(observations)) observations = [];
            if (observations.includes(stateKey)) return;
        }
        const queue = read();
        if (!queue.some(item => item.uuid === uuid && item.event.eventId === eventId)) {
            queue.push({ uuid, queuedAt: Date.now(), event: { eventId, name, ...details, ...release, clientOccurredAt: Date.now() } });
            write(queue);
            if (stateKey) {
                // Mark only after the durable queue accepted the event; retries retain its ID.
                const stored = read().some(item => item.uuid === uuid && item.event.eventId === eventId);
                if (stored) {
                    try { localStorage.setItem(OBSERVATIONS, JSON.stringify([...observations, stateKey].slice(-100))); } catch {}
                }
            }
        }
        void flushProductEvents();
    } catch {} // Analytics never blocks the action being measured.
}
export async function flushProductEvents() {
    if (running || typeof window.fetch !== 'function' || navigator.onLine === false) return;
    running = true;
    clearTimeout(timer);
    try {
        for (let n = 0; n < 20; n += 1) {
            const queue = read();
            const item = queue[0];
            if (!item) break;
            // Never replay old-account/old-installation events under a new identity.
            if (item.uuid !== localStorage.getItem('pill_reminder_uuid') || Date.now() - item.queuedAt > 86400000) { write(queue.slice(1)); continue; }
            const controller = new AbortController();
            const timeout = setTimeout(() => controller.abort(), 5000);
            let response;
            try {
                response = await window.fetch(`/api/pill-reminder/devices/${encodeURIComponent(item.uuid)}/product-events`, {
                    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(item.event), signal: controller.signal,
                });
            } finally { clearTimeout(timeout); }
            if (!response.ok && (response.status >= 500 || response.status === 429)) break;
            write(read().filter(x => !(x.uuid === item.uuid && x.event.eventId === item.event.eventId)));
        }
    } catch {} finally {
        running = false;
        if (read().length) timer = setTimeout(flushProductEvents, 30000);
    }
}
export function clearProductEvents() {
    clearTimeout(timer);
    try {
        localStorage.removeItem(KEY);
        localStorage.removeItem(OBSERVATIONS);
        localStorage.setItem('pill_product_identity_epoch', analyticsId());
    } catch {}
}
